/**
 * combo-builder-cart.js
 * Isolated module for all Shopify AJAX Cart API interaction.
 * No DOM/state knowledge beyond what's passed in — reusable independent
 * of combo-builder.js's selection logic.
 */
(function (window) {
  'use strict';

  /**
   * Adds a list of variants to the cart as one request, tagged with a
   * shared combo id so the Shopify Function (Phase 9) can identify and
   * discount them as a set.
   * @param {Array<{variantId: number|string, price: number}>} items
   * @param {{comboId: string, comboOfferName: string}} meta
   * @returns {Promise<Object>} parsed /cart/add.js response
   */
  async function addComboToCart(items, meta) {
    if (!Array.isArray(items) || items.length === 0) {
      throw new Error('addComboToCart: items array is required');
    }

    const payload = {
      items: items.map(function (item) {
        return {
          id: item.variantId,
          quantity: 1,
          properties: {
            _combo_id: meta.comboId,
            _combo_offer: meta.comboOfferName || 'Combo Offer'
          }
        };
      })
    };

    const response = await fetch(window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root + 'cart/add.js'
      : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      let errorBody = null;
      try { errorBody = await response.json(); } catch (e) { /* noop */ }
      const err = new Error(
        (errorBody && (errorBody.description || errorBody.message)) ||
        'Cart add failed with status ' + response.status
      );
      err.status = response.status;
      err.body = errorBody;
      throw err;
    }

    const data = await response.json();

    // Notify the rest of the theme (cart drawer, cart count bubble, etc.)
    // Many OS 2.0 themes listen for one of these; dispatching both covers
    // the two most common conventions without assuming the merchant's
    // exact theme internals.
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data } }));
    document.dispatchEvent(new CustomEvent('cart:refresh'));

    // Horizon and its theme family (Savor, Heritage, Atelier, Vessel,
    // Pitch, Ritual, Fabric, Tinker, Dwell) use their own event class from
    // a theme-provided module instead of listening for generic
    // CustomEvents — without this, their cart drawer/count bubble never
    // learns the cart changed until a full page reload. Wrapped in
    // try/catch and a dynamic import so this is a silent no-op on any
    // other theme that doesn't have this module at all.
    try {
      const { CartUpdateEvent } = await import('@theme/events');
      const freshCart = await (await fetch('/cart.js')).json();
      const event = new CartUpdateEvent(freshCart, 'combo-builder', {
        itemCount: freshCart.item_count,
        source: 'combo-builder',
        sections: {}
      });
      document.dispatchEvent(event);
      const cartDrawer = document.querySelector('cart-drawer-component');
      if (cartDrawer && cartDrawer.hasAttribute('auto-open') && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }
    } catch (e) {
      // Not a Horizon-family theme, or the module path has changed —
      // safe to ignore; the generic events above already fired.
    }

    return data;
  }

  /** Fetches the current cart state — used for a defensive re-check before add. */
  async function getCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Failed to fetch cart');
    return response.json();
  }

  window.ComboBuilderCart = { addComboToCart, getCart };
})(window);
